<template>
  <div id="app" ref="appContainer" class="app-container">
    <!-- 游戏容器 -->
    <div class="game-container" ref="gameContainer">
      <!-- 动态渲染当前视图组件 -->
      <component
        :is="store.currentView"
        @changeView="store.changeView"
        @openAuth="store.changeView('UserAuth')"
        @close="store.currentView === 'UserAuth' && store.changeView('GameStart')"
      ></component>

      <OpeningSequence v-if="showOpening" @complete="completeOpening" />

      <!-- ✅ 全局模态窗挂载区 -->
      <ForceLogoutModal v-if="store.modals.ForceLogout" />

      <BottomToast
        :show="store.toast.show"
        :message="store.toast.message"
        :type="store.toast.type"
      />

      <!--
      <BackpackModal v-if="store.modals.Backpack" />
      <DrawModal v-if="store.modals.Draw" @close="store.closeModal('Draw')" />
      -->
      <!-- 可继续添加其他模态窗 -->
    </div>
  </div>
</template>

<script>
import { useViewStore } from '@/stores/viewStore'
import { onMounted, onBeforeUnmount, ref, defineAsyncComponent, nextTick } from 'vue'
import { useScreenUtilsStore } from '@/stores/screenUtilsStore' // ⭐屏幕适配工具
import OpeningSequence from './ui/OpeningSequence.vue'
import { shouldShowOpening, rememberOpening } from './ui/motion'

// 全局小组件（体积小，没必要懒加载）
import ForceLogoutModal from '@/ForceLogoutModal.vue'
import BottomToast from '@/components/toast/BottomToast.vue'

// ⭐ 大页面组件统一懒加载，减小首屏 & 常驻内存
const UserHome = defineAsyncComponent(() => import('./components/UserHome.vue'))
const UserBackpack = defineAsyncComponent(() => import('./components/UserBackpack.vue'))
const UserSettings = defineAsyncComponent(() => import('./components/UserSettings.vue'))
const GameStart = defineAsyncComponent(() => import('./components/GameStart.vue'))
const UserAuth = defineAsyncComponent(() => import('./components/UserAuth.vue'))
const TutorialView = defineAsyncComponent(() => import('./components/homescreen/tutorial/TutorialView.vue'))
const TrialTower = defineAsyncComponent(() => import('@/components/homescreen/worldmap/TrialTower.vue'))
const WorldMapSelect = defineAsyncComponent(() => import('@/components/homescreen/worldmap/WorldMapSelect.vue'))
const MapMain = defineAsyncComponent(() => import('@/map/MapMain.vue'))

const AdminHome = defineAsyncComponent(() =>
  import('@/admin/AdminHome.vue')
)
const AdminAbilityManager = defineAsyncComponent(() =>
  import('@/admin/AdminAbilityManager.vue')
)
const AdminMoveManager = defineAsyncComponent(() =>
  import('@/admin/AdminMoveManager.vue')
)
const AdminPokemonManager = defineAsyncComponent(() =>
  import('@/admin/AdminPokemonManager.vue')
)


export default {
  name: 'App',
  components: {
    OpeningSequence,
    UserHome,
    UserBackpack,
    UserSettings,
    GameStart,
    UserAuth,
    TutorialView,
    TrialTower,
    WorldMapSelect,
    BottomToast,
    ForceLogoutModal,
    MapMain,
        // ✅ 加上这四个
        AdminHome,
    AdminAbilityManager,
    AdminMoveManager,
    AdminPokemonManager,
  },
  setup() {
    const store = useViewStore()
    const screenUtils = useScreenUtilsStore()
    const appContainer = ref(null)
    const gameContainer = ref(null)
    const showOpening = ref(shouldShowOpening())
    const completeOpening = () => {
      rememberOpening()
      showOpening.value = false
      nextTick(() => appContainer.value?.querySelector('.start-text')?.focus({ preventScroll: true }))
    }

    /**
     * 横竖屏适配
     * 横屏时：正常显示
     * 竖屏时：整体旋转 90° 让游戏横屏化
     */
    const adjustGameLayout = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      let gameWidth, gameHeight

      if (width > height) {
        // 横屏
        gameWidth = width
        gameHeight = height
        appContainer.value.style.transform = 'rotate(0deg)'
        store.setRotated(false)
      } else {
        // 竖屏
        gameWidth = height
        gameHeight = width
        appContainer.value.style.transform = 'rotate(90deg)'
        store.setRotated(true)
      }

      appContainer.value.style.transformOrigin = 'center'
      appContainer.value.style.width = `${gameWidth}px`
      appContainer.value.style.height = `${gameHeight}px`
      appContainer.value.style.position = 'absolute'
      appContainer.value.style.top = `${(window.innerHeight - gameHeight) / 2}px`
      appContainer.value.style.left = `${(window.innerWidth - gameWidth) / 2}px`

      // ⭐同步尺寸到 screenUtilsStore，让其他地方知道当前画布尺寸
      screenUtils.updateSize()
    }

    onMounted(() => {
      adjustGameLayout()
      window.addEventListener('resize', adjustGameLayout)
      window.addEventListener('orientationchange', adjustGameLayout)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', adjustGameLayout)
      window.removeEventListener('orientationchange', adjustGameLayout)
    })

    return {
      store,
      appContainer,
      gameContainer,
      showOpening,
      completeOpening
    }
  }
}
</script>

<style scoped>
#app {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #000;
  overflow: hidden;
}

.game-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

body, html {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

* {
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
</style>
